import { useState } from "react";
import { Panel } from "./panel";
import { Skeleton } from "../../ui/skeleton";
import { BasisToggle, type ThreeWayBasis } from "./basis-toggle";
import { IndustrySelect, YearChips } from "./chart-filters";
import { CohortDevelopmentChart } from "./charts/cohort-development-chart";
import { colorFor } from "./charts/loss-development-chart";
import { TrendLineChart, type TrendSeries } from "./charts/trend-line-chart";
import type { MonthRow } from "../../pages/PortfolioPerformance/lib/api/lifetime-loss-projections";

export interface LifetimeChartData {
  cohorts: string[];
  /** cohort year -> month labels that are realized readings; the rest of that cohort's curve is projected */
  realized: Record<string, string[]>;
  forecast: MonthRow[];
  projection: MonthRow[];
}

/** The lifetime-projection charts (Prepayment, Repeat Customer): ONE chart, drawn and behaving exactly like the Loss
 *  Development chart on the Loss Calibration page (see CohortDevelopmentChart):
 *   - the Realized / Projected / Active toggle and the industry dropdown sit in the chart's header; the page owns the
 *     toggle (`basis`), because it picks which rows of the table the page fetches (realized_tf / cohort_active_tf);
 *   - a "Funded year" chip row sits inside the panel (empty = all);
 *   - every view draws, per funded year, the realized readings (solid), the published curve past the last reading
 *     (fine dotted) and the model's projection at every month (long dashes): `consider` alone decides actual versus
 *     projected, the toggle only decides which contracts are counted. */
export function LifetimeProjectionCharts({
  data,
  isPending,
  isError,
  industry,
  onIndustryChange,
  industryOptions,
  basis,
  onBasisChange,
  forecastTitle,
  projectionTitle,
  sideBySide = false,
}: {
  data: LifetimeChartData | null | undefined;
  isPending: boolean;
  isError: boolean;
  industry: string;
  onIndustryChange: (value: string) => void;
  industryOptions: readonly string[];
  basis: ThreeWayBasis;
  onBasisChange: (value: ThreeWayBasis) => void;
  forecastTitle: string;
  /** Title of the panel next to the chart: the model's projection for the same years. */
  projectionTitle: string;
  /** Put the forecast chart and the projection chart in one row, forecast on the left and projection on the right (from the xl
   *  breakpoint up), instead of the projection under the forecast. The forecast fills the row on its own when there is no projection. */
  sideBySide?: boolean;
}) {
  const [selected, setSelected] = useState<string[]>([]);

  const years = data?.cohorts ?? [];
  const activeSelected = selected.filter((y) => years.includes(y));
  const visibleYears = activeSelected.length ? activeSelected : years;

  function toggle(year: string) {
    setSelected((prev) => (prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year]));
  }

  const chips = <YearChips years={years} selected={activeSelected} onToggle={toggle} />;

  // The projection panel next to the chart: one dashed line per visible year, the same colours as the chart above.
  const palette = years.map((_, i) => i);
  const projectionSeries: TrendSeries[] = visibleYears.map((year) => ({
    dataKey: year,
    name: year,
    color: colorFor(years.indexOf(year), palette),
    dashed: true,
  }));

  const showProjection = !!data && years.length > 0;

  return (
    <div className={sideBySide ? "grid grid-cols-1 gap-6 xl:grid-cols-2" : "space-y-6"}>
      <Panel
        className={sideBySide && !showProjection ? "min-w-0 xl:col-span-2" : "min-w-0"}
        title={forecastTitle}
        description={`${industry}. Realized solid, projected dotted.`}
        right={
          <div className="flex flex-wrap items-center gap-2">
            <BasisToggle
              value={basis}
              onChange={onBasisChange}
              realizedHint="Contracts that have matured"
              projectedHint="Contracts still running"
              activeHint="Funded years that still have active contracts"
            />
            <IndustrySelect
              value={industry}
              options={industryOptions}
              onChange={onIndustryChange}
              ariaLabel="Borrower industry"
            />
          </div>
        }
      >
        {isError ? (
          <div
            role="alert"
            className="border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          >
            Could not load this chart.
          </div>
        ) : isPending ? (
          <Skeleton className="h-64 w-full" />
        ) : data === null || !data ? (
          <p className="text-sm text-muted-foreground">Not Available Yet.</p>
        ) : years.length === 0 ? (
          <p className="text-sm text-muted-foreground">No funded years under this toggle for this industry and ticket size yet.</p>
        ) : (
          <>
            {chips}
            <CohortDevelopmentChart
              rows={data.forecast}
              projection={data.projection}
              realized={data.realized ?? {}}
              years={visibleYears}
              allYears={years}
              xTitle="Month"
              xAxisLabel="Months since origination"
              height={300}
            />
          </>
        )}
      </Panel>

      {data && showProjection ? (
        <Panel className="min-w-0" title={projectionTitle} description={`${industry}. The model's projection for the same years.`}>
          {chips}
          <TrendLineChart data={data.projection} series={projectionSeries} suffix="%" height={280} />
        </Panel>
      ) : null}
    </div>
  );
}
